import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Info, Loader2 } from 'lucide-react';
import { dashboardWidgetService } from '@services';
import { MY_DASHBOARD_KEY } from '@hooks/useMyDashboard';
import { showToast } from '@utils/toast';
import { getApiErrorMessage, getApiErrorStatus } from '@utils/apiError';
import Button from '@components/common/Button';
import Checkbox from '@components/common/Checkbox';
import ErrorState from '@components/common/ErrorState';
import SettingsSectionHeader from '@components/features/data-management-settings/SettingsSectionHeader';
import { SUBSCRIPTIONS_KEY } from './subscriptionsKey';
import styles from './WidgetAccessSettings.module.css';

const MATRIX_KEY = ['widget-access-matrix'];

const INELIGIBLE_ROLE_REASON = 'This role cannot see the data of this widget';
const SAVE_FAILED_MESSAGE = 'The change could not be saved. Please try again.';

const cellKey = (widgetKey: string, roleId: number) => `${widgetKey}:${roleId}`;

/**
 * The Admin matrix of Dashboard Widgets by roles: tick a box to give a role access to a widget, untick it to remove
 * the access. The 17 widgets are fixed and built in, so only the access can be changed. Each change is saved at
 * once and shown at once; a failed save puts the box back.
 * A widget may list the only roles it can be given to; the box of any other role is disabled, and an old grant to
 * such a role shows unticked because the server counts it as no access.
 */
const WidgetAccessSettings: React.FC = () => {
  const queryClient = useQueryClient();
  // The state a box is being saved to, shown until the save is over.
  const [pending, setPending] = useState<Record<string, boolean>>({});

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: MATRIX_KEY,
    queryFn: () => dashboardWidgetService.getAccessMatrix()
  });

  const toggle = async (widgetKey: string, roleId: number, granted: boolean) => {
    const key = cellKey(widgetKey, roleId);
    setPending((current) => ({ ...current, [key]: granted }));
    try {
      await (granted
        ? dashboardWidgetService.grantAccess(widgetKey, roleId)
        : dashboardWidgetService.revokeAccess(widgetKey, roleId));
      // Access decides what the Admin's own Dashboard and subscriptions show too.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: MATRIX_KEY }),
        queryClient.invalidateQueries({ queryKey: MY_DASHBOARD_KEY }),
        queryClient.invalidateQueries({ queryKey: SUBSCRIPTIONS_KEY })
      ]);
    } catch (error) {
      // A 400 is the server refusing the grant for this role; its message says why.
      showToast.error(getApiErrorStatus(error) === 400 ? getApiErrorMessage(error, SAVE_FAILED_MESSAGE) : SAVE_FAILED_MESSAGE);
    } finally {
      setPending((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
    }
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className={styles.loading} data-test-id="widget-access-loading">
          <Loader2 size={32} className="spin" />
          <p>Loading Widget Access...</p>
        </div>
      );
    }
    if (isError || !data) {
      return (
        <ErrorState
          data-test-id="widget-access-error"
          icon={<AlertCircle size={24} />}
          message="Failed to load Widget Access."
          action={
            <Button variant="secondary" size="sm" onClick={() => refetch()}>
              Try again
            </Button>
          }
        />
      );
    }

    const granted = new Set(data.access.map((entry) => cellKey(entry.widget_key, entry.role_id)));

    return (
      <div className={styles.tableWrapper}>
        <table className={styles.table} data-test-id="widget-access-matrix">
          <thead>
            <tr>
              <th scope="col">Widget</th>
              {data.roles.map((role) => (
                <th key={role.id} scope="col" className={styles.roleHeader}>
                  {role.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.widgets.map((widget) => (
              <tr key={widget.key}>
                <th scope="row" className={styles.widgetName}>
                  {widget.title}
                </th>
                {data.roles.map((role) => {
                  const key = cellKey(widget.key, role.id);
                  const eligible = !widget.grantable_roles || widget.grantable_roles.includes(role.name);
                  const checked = eligible && (pending[key] ?? granted.has(key));
                  const reason = eligible ? undefined : INELIGIBLE_ROLE_REASON;
                  return (
                    <td key={role.id} className={styles.cell} title={reason}>
                      <Checkbox
                        checked={checked}
                        disabled={!eligible || key in pending}
                        title={reason}
                        onChange={() => toggle(widget.key, role.id, !checked)}
                        aria-label={`${widget.title} for ${role.name}`}
                        dataTestId={`widget-access-${widget.key}-${role.id}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className={styles.container} data-test-id="widget-access-settings">
      <SettingsSectionHeader
        title="Widget Access"
        description="Choose which roles can use each Dashboard Widget. A widget can be available to several roles."
      />
      <div className={styles.note} data-test-id="widget-access-note">
        <Info size={18} className={styles.noteIcon} />
        <span>
          Removing access hides the widget from the Dashboards of users who no longer have access through any of their
          roles, stops its data for them and pauses their email subscriptions to it. Giving access back restores all of
          it.
        </span>
      </div>
      {renderBody()}
    </div>
  );
};

export { WidgetAccessSettings };
